import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface CompanyProfile {
  id: string;
  name: string;
  legal_name: string | null;
  logo_url: string | null;
  logo_path: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  phone: string | null;
  website: string | null;
  footer_text: string | null;
}

export const useCompanyProfile = () => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["company_profile"],
    queryFn: async () => {
      const { data: companyId } = await supabase.rpc("my_company_id");
      if (!companyId) return null;
      const { data, error } = await supabase
        .from("companies")
        .select("id, name, legal_name, logo_url, logo_path, address, city, state, zip, phone, website, footer_text")
        .eq("id", companyId)
        .single();
      if (error) throw error;
      return data as unknown as CompanyProfile;
    },
    enabled: !!user,
  });
};

export const useUpdateCompanyProfile = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (updates: Partial<Omit<CompanyProfile, "id">>) => {
      const { data: companyId } = await supabase.rpc("my_company_id");
      if (!companyId) throw new Error("Company not found");
      const { error } = await supabase
        .from("companies")
        .update(updates as any)
        .eq("id", companyId);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["company_profile"] }),
  });
};

export const useUploadCompanyLogo = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const { data: companyId } = await supabase.rpc("my_company_id");
      if (!companyId) throw new Error("Company not found");

      // Get current logo_path to delete old file
      const { data: current } = await supabase
        .from("companies")
        .select("logo_path")
        .eq("id", companyId)
        .single();

      if ((current as any)?.logo_path) {
        await supabase.storage.from("company-logos").remove([(current as any).logo_path]);
      }

      const ext = file.name.split(".").pop() || "png";
      const filePath = `${companyId}/logo_${Date.now()}.${ext}`;
      const { error: uploadErr } = await supabase.storage.from("company-logos").upload(filePath, file);
      if (uploadErr) throw uploadErr;

      const { data: urlData } = supabase.storage.from("company-logos").getPublicUrl(filePath);

      const { error } = await supabase
        .from("companies")
        .update({ logo_url: urlData.publicUrl, logo_path: filePath } as any)
        .eq("id", companyId);
      if (error) throw error;

      return urlData.publicUrl;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["company_profile"] }),
  });
};
